import { Outlet } from "react-router";
import { useFeatureGate, useFeatureGateContext } from "@/contexts/FeatureGateContext";
import { UpgradeWall } from "@/components/shared/UpgradeWall";
import type { PracticeFeatureKey } from "@/types/practice";

interface FeatureRouteGuardProps {
    featureKey: PracticeFeatureKey;
    children?: React.ReactNode;
}

/**
 * FeatureRouteGuard — prüft ob ein Feature im aktuellen Plan verfügbar und aktiviert ist.
 *
 * - enabled → zeigt children / Outlet
 * - !enabled && inPlan → Aktivierungs-Hinweis (Feature ist deaktiviert, aber im Plan)
 * - !enabled && !inPlan → Upgrade-Wall (Rule 177: kein 403, sondern Upgrade-Prompt)
 * - loading → Lade-Spinner
 */
export function FeatureRouteGuard({ featureKey, children }: FeatureRouteGuardProps) {
    const { loading } = useFeatureGateContext();
    const status = useFeatureGate(featureKey);

    if (loading) {
        return (
            <div className="flex h-full min-h-[400px] items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
                    <p className="text-sm text-neutral-500">Wird geladen…</p>
                </div>
            </div>
        );
    }

    // Feature aktiviert → Inhalt anzeigen
    if (status.enabled) {
        return children ? <>{children}</> : <Outlet />;
    }

    // Feature im Plan aber deaktiviert → Aktivierungs-Hinweis
    if (status.inPlan && !status.enabled) {
        return (
            <UpgradeWall
                featureKey={featureKey}
                variant="disabled"
            />
        );
    }

    // Feature nicht im Plan → Upgrade-Wall
    return (
        <UpgradeWall
            featureKey={featureKey}
            variant="upgrade"
        />
    );
}
